import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface ActiveDelivery {
  orderId: string;
  deliveryBoyName: string;
  deliveryBoyPhone: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  customerCity: string;
  customerLat?: number;
  customerLng?: number;
  deliveryLat?: number;
  deliveryLng?: number;
  finalAmount: number;
  status: string;
  updatedAt: string;
}

function calcDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const STATUS_COLORS: Record<string, string> = {
  shipped: 'bg-orange-500',
  confirmed: 'bg-sky-500',
  pending: 'bg-amber-500',
};

export default function LiveDeliveryMap() {
  const [deliveries, setDeliveries] = useState<ActiveDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState<ActiveDelivery | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchActiveDeliveries = useCallback(async () => {
    const { data, error } = await supabase
      .from('orders')
      .select(
        'id, delivery_boy_name, delivery_boy_phone, customer_name, customer_phone, customer_address, customer_city, customer_lat, customer_lng, delivery_lat, delivery_lng, final_amount, status, updated_at'
      )
      .in('status', ['shipped', 'confirmed'])
      .not('delivery_boy_phone', 'is', null)
      .order('updated_at', { ascending: false });

    if (!error && data) {
      const mapped: ActiveDelivery[] = data.map((row: {
        id: string;
        delivery_boy_name: string | null;
        delivery_boy_phone: string | null;
        customer_name: string;
        customer_phone: string;
        customer_address: string;
        customer_city: string;
        customer_lat?: number | null;
        customer_lng?: number | null;
        delivery_lat?: number | null;
        delivery_lng?: number | null;
        final_amount: number;
        status: string;
        updated_at: string;
      }) => ({
        orderId: row.id,
        deliveryBoyName: row.delivery_boy_name || 'Unknown',
        deliveryBoyPhone: row.delivery_boy_phone || '',
        customerName: row.customer_name,
        customerPhone: row.customer_phone,
        customerAddress: row.customer_address,
        customerCity: row.customer_city,
        customerLat: row.customer_lat ?? undefined,
        customerLng: row.customer_lng ?? undefined,
        deliveryLat: row.delivery_lat ?? undefined,
        deliveryLng: row.delivery_lng ?? undefined,
        finalAmount: row.final_amount,
        status: row.status,
        updatedAt: row.updated_at,
      }));
      setDeliveries(mapped);
      setLastRefresh(new Date());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchActiveDeliveries();
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    intervalRef.current = setInterval(fetchActiveDeliveries, 10000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchActiveDeliveries]);

  const withLocation = deliveries.filter(d => d.deliveryLat && d.deliveryLng);
  const withoutLocation = deliveries.filter(d => !d.deliveryLat || !d.deliveryLng);

  // Build map URL showing all delivery boys
  const buildMapUrl = (delivery: ActiveDelivery): string => {
    if (delivery.deliveryLat && delivery.deliveryLng) {
      return `https://maps.google.com/maps?q=${delivery.deliveryLat},${delivery.deliveryLng}&z=15&output=embed`;
    }
    if (delivery.customerLat && delivery.customerLng) {
      return `https://maps.google.com/maps?q=${delivery.customerLat},${delivery.customerLng}&z=14&output=embed`;
    }
    return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110204.5877174017!2d77.934733!3d30.325565!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390929c356c888af%3A0x4c3562c03251865!2sDehradun%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin`;
  };

  const getTimeSince = (updatedAt: string) => {
    const diff = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
            <i className="ri-map-2-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Live Delivery Map</p>
            <p className="text-gray-400 text-xs">Saare active deliveries real-time</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-xs flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block"></span>
            Auto refresh: 10s
          </span>
          <button
            onClick={fetchActiveDeliveries}
            className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all"
          >
            <i className={`ri-refresh-line text-gray-600 ${loading ? 'animate-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-3 text-center">
          <p className="text-orange-700 font-bold text-xl">{deliveries.filter(d => d.status === 'shipped').length}</p>
          <p className="text-orange-500 text-xs mt-0.5">Out for Delivery</p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-2xl p-3 text-center">
          <p className="text-green-700 font-bold text-xl">{withLocation.length}</p>
          <p className="text-green-500 text-xs mt-0.5">Live Location ON</p>
        </div>
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3 text-center">
          <p className="text-gray-700 font-bold text-xl">{deliveries.length}</p>
          <p className="text-gray-500 text-xs mt-0.5">Total Active</p>
        </div>
      </div>

      {/* Last refresh */}
      <div className="flex items-center justify-between text-xs text-gray-400 px-1">
        <span className="flex items-center gap-1">
          <i className="ri-time-line"></i>
          Last updated: {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
        <span>{withLocation.length} / {deliveries.length} with GPS</span>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Live data load ho raha hai...</p>
        </div>
      ) : deliveries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center">
            <i className="ri-e-bike-2-line text-3xl text-green-400"></i>
          </div>
          <div className="text-center">
            <p className="text-gray-700 font-semibold">Koi active delivery nahi!</p>
            <p className="text-gray-400 text-sm mt-1">Jab delivery boy order pick karega, yahan live dikhega</p>
          </div>
        </div>
      ) : (
        <>
          {/* Delivery Cards with Maps */}
          <div className="space-y-4">
            {deliveries.map(delivery => {
              const isSelected = selectedDelivery?.orderId === delivery.orderId;
              const distance = delivery.deliveryLat && delivery.deliveryLng && delivery.customerLat && delivery.customerLng
                ? calcDistanceKm(delivery.deliveryLat, delivery.deliveryLng, delivery.customerLat, delivery.customerLng)
                : null;

              return (
                <div
                  key={delivery.orderId}
                  className={`bg-white rounded-2xl border overflow-hidden transition-all ${isSelected ? 'border-gray-400' : 'border-gray-100 hover:border-gray-200'}`}
                >
                  {/* Status bar */}
                  <div className={`h-1.5 w-full ${STATUS_COLORS[delivery.status] ?? 'bg-gray-300'}`}></div>

                  {/* Card Header */}
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => setSelectedDelivery(isSelected ? null : delivery)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Delivery boy avatar */}
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-bold text-base shrink-0">
                        {delivery.deliveryBoyName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-gray-900 font-bold text-sm">{delivery.deliveryBoyName}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${STATUS_COLORS[delivery.status] ?? 'bg-gray-400'}`}>
                            {delivery.status === 'shipped' ? 'Out for Delivery' : 'Confirmed'}
                          </span>
                        </div>
                        <p className="text-gray-400 text-xs mt-0.5">{delivery.deliveryBoyPhone}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {delivery.deliveryLat && delivery.deliveryLng ? (
                            <span className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                              Live GPS · {getTimeSince(delivery.updatedAt)}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-gray-400 text-xs">
                              <i className="ri-signal-wifi-off-line"></i>GPS inactive
                            </span>
                          )}
                          {distance !== null && (
                            <span className="flex items-center gap-1 text-orange-500 text-xs font-semibold">
                              <i className="ri-navigation-line"></i>
                              {distance.toFixed(1)} km from customer
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-gray-900 font-bold text-sm">₹{delivery.finalAmount}</p>
                        <p className="text-gray-400 text-xs">COD</p>
                        <i className={`${isSelected ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-lg`}></i>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info Row */}
                  <div className="px-4 pb-3">
                    <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-user-3-line text-gray-400 shrink-0 mt-0.5"></i>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-700 text-xs font-semibold">{delivery.customerName}</p>
                        <p className="text-gray-400 text-xs truncate">{delivery.customerAddress}, {delivery.customerCity}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a
                          href={`tel:${delivery.deliveryBoyPhone}`}
                          onClick={e => e.stopPropagation()}
                          className="w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center cursor-pointer hover:bg-green-200 transition-all"
                          title="Call Delivery Boy"
                        >
                          <i className="ri-e-bike-2-fill text-green-600 text-sm"></i>
                        </a>
                        <a
                          href={`tel:${delivery.customerPhone}`}
                          onClick={e => e.stopPropagation()}
                          className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all"
                          title="Call Customer"
                        >
                          <i className="ri-phone-line text-gray-600 text-sm"></i>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Map */}
                  {isSelected && (
                    <div className="border-t border-gray-100">
                      {/* Map */}
                      <div className="relative" style={{ height: '240px' }}>
                        <iframe
                          src={buildMapUrl(delivery)}
                          width="100%"
                          height="240"
                          style={{ border: 0 }}
                          loading="lazy"
                          title={`Delivery ${delivery.orderId}`}
                          allowFullScreen
                        ></iframe>

                        {/* Live badge overlay */}
                        {delivery.deliveryLat && delivery.deliveryLng && (
                          <div className="absolute top-3 left-3 pointer-events-none">
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 text-green-700 text-xs font-bold shadow">
                              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                              LIVE TRACKING
                            </span>
                          </div>
                        )}

                        {/* Google Maps link */}
                        {delivery.deliveryLat && delivery.deliveryLng && (
                          <a
                            href={`https://www.google.com/maps/dir/${delivery.deliveryLat},${delivery.deliveryLng}/${delivery.customerLat ?? ''},${delivery.customerLng ?? ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-900 text-white text-xs font-bold shadow hover:bg-gray-700 transition-all"
                            onClick={e => e.stopPropagation()}
                          >
                            <i className="ri-navigation-fill"></i>
                            Open in Maps
                          </a>
                        )}
                      </div>

                      {/* Detail Rows */}
                      <div className="p-4 space-y-3">
                        {/* Delivery Boy location info */}
                        {delivery.deliveryLat && delivery.deliveryLng && (
                          <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <i className="ri-e-bike-2-fill text-orange-500 text-sm"></i>
                              <p className="text-orange-700 font-bold text-xs">Delivery Boy Location</p>
                              <span className="ml-auto text-orange-400 text-xs">{getTimeSince(delivery.updatedAt)}</span>
                            </div>
                            <p className="text-orange-600 text-xs font-mono">
                              {delivery.deliveryLat.toFixed(5)}, {delivery.deliveryLng.toFixed(5)}
                            </p>
                          </div>
                        )}

                        {/* Customer location info */}
                        {delivery.customerLat && delivery.customerLng && (
                          <div className="bg-green-50 border border-green-100 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <i className="ri-home-5-fill text-green-500 text-sm"></i>
                              <p className="text-green-700 font-bold text-xs">Customer Location</p>
                            </div>
                            <p className="text-green-600 text-xs font-mono">
                              {delivery.customerLat.toFixed(5)}, {delivery.customerLng.toFixed(5)}
                            </p>
                            <p className="text-green-600 text-xs mt-1">{delivery.customerAddress}</p>
                          </div>
                        )}

                        {/* Distance indicator */}
                        {distance !== null && (
                          <div className={`rounded-xl p-3 flex items-center gap-3 ${distance <= 1 ? 'bg-green-50 border border-green-200' : distance <= 3 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-100'}`}>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${distance <= 1 ? 'bg-green-100' : distance <= 3 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                              <i className={`ri-navigation-line text-lg ${distance <= 1 ? 'text-green-600' : distance <= 3 ? 'text-amber-600' : 'text-gray-500'}`}></i>
                            </div>
                            <div>
                              <p className={`font-bold text-sm ${distance <= 1 ? 'text-green-700' : distance <= 3 ? 'text-amber-700' : 'text-gray-700'}`}>
                                {distance.toFixed(2)} km remaining
                              </p>
                              <p className={`text-xs ${distance <= 1 ? 'text-green-500' : distance <= 3 ? 'text-amber-500' : 'text-gray-400'}`}>
                                {distance <= 1 ? 'Almost there! Delivery imminent' : distance <= 3 ? 'Very close to customer' : 'On the way'}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Order ID */}
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span className="font-mono">Order: #{delivery.orderId.toUpperCase().slice(0, 8)}</span>
                          <span>Updated: {getTimeSince(delivery.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Deliveries without GPS */}
          {withoutLocation.length > 0 && !loading && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-signal-wifi-off-line text-amber-500"></i>
                <p className="text-amber-700 font-semibold text-sm">GPS Inactive — {withoutLocation.length} delivery boy(s)</p>
              </div>
              <div className="space-y-2">
                {withoutLocation.map(d => (
                  <div key={d.orderId} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-amber-100">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-sm shrink-0">
                      {d.deliveryBoyName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 text-xs font-semibold">{d.deliveryBoyName}</p>
                      <p className="text-gray-400 text-xs truncate">Delivering to {d.customerName}</p>
                    </div>
                    <span className="text-amber-500 text-xs font-bold whitespace-nowrap">No GPS</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
